import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';

const EMPTY = { name: '', email: '', phone: '', address: '', plan: 'basic', dashboard_mode: 'mall' };

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'create' | 'edit'
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/customers', { params: { search, page, limit: 15 } });
      setCustomers(data.data);
      setTotal(data.pagination.total);
    } finally { setLoading(false); }
  }, [search, page]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(EMPTY); setEditId(null); setModal('create'); };
  const openEdit = (c) => { setForm({ name: c.name, email: c.email, phone: c.phone || '', address: c.address || '', plan: c.plan, dashboard_mode: c.dashboard_mode || 'mall' }); setEditId(c.id); setModal('edit'); };
  const close = () => setModal(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (modal === 'create') await api.post('/customers', form);
      else await api.put(`/customers/${editId}`, form);
      close(); load();
    } catch (err) {
      alert(err.response?.data?.message || 'Hata oluştu');
    } finally { setSaving(false); }
  };

  const toggleActive = async (c) => {
    if (!window.confirm(`Müşteriyi ${c.is_active ? 'devre dışı bırakmak' : 'aktifleştirmek'} istediğinizden emin misiniz?`)) return;
    await api.put(`/customers/${c.id}`, { is_active: c.is_active ? 0 : 1 });
    load();
  };

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <h1>Müşteriler</h1>
          <p>{total} müşteri kayıtlı</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>+ Müşteri Ekle</button>
      </div>

      <div className="card">
        <div style={{ marginBottom: 16 }}>
          <input placeholder="Müşteri ara..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} style={{ maxWidth: 300 }} />
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Müşteri</th><th>Email</th><th>Plan</th><th>Tip</th><th>Cihaz</th><th>Kullanıcı</th><th>Durum</th><th>İşlem</th></tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--text3)' }}>Yükleniyor...</td></tr>
              ) : customers.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--text3)' }}>Müşteri bulunamadı</td></tr>
              ) : customers.map(c => (
                <tr key={c.id}>
                  <td><strong>{c.name}</strong></td>
                  <td style={{ color: 'var(--text2)' }}>{c.email}</td>
                  <td><span className="badge badge-blue">{c.plan}</span></td>
                  <td>
                    <span className="badge" style={{
                      background: c.dashboard_mode === 'store_group' ? 'rgba(139,92,246,0.15)' : 'rgba(59,130,246,0.12)',
                      color:      c.dashboard_mode === 'store_group' ? '#a78bfa'                : 'var(--accent)',
                    }}>
                      {c.dashboard_mode === 'store_group' ? 'Mağaza' : 'AVM'}
                    </span>
                  </td>
                  <td>{c.device_count}</td>
                  <td>{c.user_count}</td>
                  <td>
                    <span className={`badge ${c.is_active ? 'badge-green' : 'badge-red'}`}>
                      {c.is_active ? 'Aktif' : 'Pasif'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => openEdit(c)}>Düzenle</button>
                      <button className={`btn btn-sm ${c.is_active ? 'btn-danger' : 'btn-secondary'}`} onClick={() => toggleActive(c)}>
                        {c.is_active ? 'Devre Dışı' : 'Aktifleştir'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {total > 15 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
            <button className="btn btn-secondary btn-sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Önceki</button>
            <span style={{ lineHeight: '30px', fontSize: 13, color: 'var(--text2)' }}>Sayfa {page}</span>
            <button className="btn btn-secondary btn-sm" disabled={page * 15 >= total} onClick={() => setPage(p => p + 1)}>Sonraki →</button>
          </div>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && close()}>
          <div className="modal admin-form-modal">
            <div className="modal-header">
              <h3>{modal === 'create' ? 'Yeni Müşteri' : 'Müşteri Düzenle'}</h3>
              <button className="btn btn-secondary btn-icon" onClick={close}>✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group"><label>Müşteri Adı *</label><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required /></div>
              <div className="form-group"><label>Email *</label><input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required /></div>
              <div className="form-row">
                <div className="form-group"><label>Telefon</label><input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} /></div>
                <div className="form-group">
                  <label>Plan</label>
                  <select value={form.plan} onChange={e => setForm(f => ({ ...f, plan: e.target.value }))}>
                    <option value="basic">Basic</option>
                    <option value="pro">Pro</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Dashboard Tipi</label>
                <select value={form.dashboard_mode || 'mall'} onChange={e => setForm(f => ({ ...f, dashboard_mode: e.target.value }))}>
                  <option value="mall">AVM / Çok Kapılı Yapı</option>
                  <option value="store_group">Mağaza / Çok Şubeli Yapı</option>
                </select>
              </div>
              <div className="form-group"><label>Adres</label><textarea value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} rows={2} style={{ resize: 'vertical' }} /></div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
                <button type="button" className="btn btn-secondary" onClick={close}>İptal</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
